import {
  JournalEntry,
  Decision,
  Pattern,
  SemanticLink,
  LinkType,
  PatternType,
  LifeContext,
  DecisionOutcome
} from '../types';
import { getPrompts } from '../constants/aiPrompts';

type ChatMessageContent = string | Array<{ type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string; detail?: string } }>;
type ChatMessage = { role: 'system' | 'user' | 'assistant'; content: ChatMessageContent };

async function aiChatCompletion(
  messages: ChatMessage[], 
  options: { temperature?: number; maxTokens?: number; jsonMode?: boolean; hasImage?: boolean } = {}
): Promise<string> {
  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ 
      messages, 
      temperature: options.temperature, 
      maxTokens: options.maxTokens,
      jsonMode: options.jsonMode,
      hasImage: options.hasImage
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'AI request failed');
  }
  const data = await response.json();
  return data.content || '';
}

async function aiEmbedding(text: string): Promise<number[]> {
  const response = await fetch('/api/ai/embedding', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ text }),
  });
  if (!response.ok) return [];
  const data = await response.json();
  return data.embedding || [];
}

async function aiBatchEmbeddings(texts: string[]): Promise<number[][]> {
  const response = await fetch('/api/ai/embeddings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ texts }),
  });
  if (!response.ok) return [];
  const data = await response.json();
  return data.embeddings || [];
}

/**
 * SEMANTIC GRAPH SERVICE
 *
 * Questo servizio costruisce e mantiene il grafo semantico della vita dell'utente:
 * - Identifica pattern ricorrenti
 * - Collega momenti simili o correlati
 * - Traccia outcome di decisioni
 * - Avvisa quando l'utente sta per ripetere un errore
 */

// ============================================
// PATTERN DETECTION
// ============================================

const getPatternEmbedding = async (text: string): Promise<number[]> => {
  try {
    return await aiEmbedding(text);
  } catch (error) {
    console.error('Embedding error:', error);
    return [];
  }
};

const cosineSimilarity = (a: number[], b: number[]): number => {
  if (a.length !== b.length || a.length === 0) return 0;
  let dotProduct = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB) || 1);
};

const deduplicatePatternsWithEmbeddings = async (
  patterns: Pattern[],
  existingPatterns: Pattern[] = []
): Promise<Pattern[]> => {
  const allPatterns = [...patterns, ...existingPatterns];
  if (allPatterns.length <= 1) return allPatterns;

  // Lowered threshold from 0.78 to 0.72 to catch more similar patterns
  // This prevents patterns like "Sunday anxiety" and "Weekend stress" from being separate
  const SEMANTIC_THRESHOLD = 0.72;
  const patternTexts = allPatterns.map(p => `${p.title}. ${p.description}`);
  
  let embeddings: number[][] = [];
  try {
    embeddings = await aiBatchEmbeddings(patternTexts);
    if (embeddings.length === 0) {
      return deduplicatePatternsFallback(allPatterns);
    }
  } catch (error) {
    console.error('Batch embedding error, falling back to basic dedup:', error);
    return deduplicatePatternsFallback(allPatterns);
  }
  
  const uniquePatterns: Pattern[] = [];
  const usedIndices = new Set<number>();
  const newPatternCount = patterns.length;
  
  const sortedIndices = allPatterns
    .map((p, i) => ({ 
      index: i, 
      strength: p.strength,
      isNew: i < newPatternCount
    }))
    .sort((a, b) => {
      if (a.isNew !== b.isNew) return a.isNew ? -1 : 1;
      return b.strength - a.strength;
    })
    .map(item => item.index);
  
  for (const idx of sortedIndices) {
    if (usedIndices.has(idx)) continue;
    
    const currentPattern = allPatterns[idx];
    const currentEmbedding = embeddings[idx];
    
    const duplicateIndices: number[] = [idx];
    
    for (let j = 0; j < allPatterns.length; j++) {
      if (j === idx || usedIndices.has(j)) continue;
      
      const similarity = cosineSimilarity(currentEmbedding, embeddings[j]);
      if (similarity >= SEMANTIC_THRESHOLD) {
        duplicateIndices.push(j);
        usedIndices.add(j);
      }
    }
    
    usedIndices.add(idx);
    
    if (duplicateIndices.length > 1) {
      const mergedEntries = [...new Set(
        duplicateIndices.flatMap(i => allPatterns[i].relatedEntries)
      )];
      const maxFrequency = Math.max(...duplicateIndices.map(i => allPatterns[i].frequency));
      const maxStrength = Math.max(...duplicateIndices.map(i => allPatterns[i].strength));
      const oldestFirstSeen = Math.min(...duplicateIndices.map(i => allPatterns[i].firstSeen));
      const newestLastSeen = Math.max(...duplicateIndices.map(i => allPatterns[i].lastSeen));
      
      uniquePatterns.push({
        ...currentPattern,
        frequency: maxFrequency,
        strength: maxStrength,
        firstSeen: oldestFirstSeen,
        lastSeen: newestLastSeen,
        relatedEntries: mergedEntries,
      });
    } else {
      uniquePatterns.push(currentPattern);
    }
  }
  
  return uniquePatterns;
};

/**
 * Improved fallback deduplication for patterns when embeddings fail
 * Uses multiple strategies: word overlap, fuzzy matching, and type-based grouping
 */
const deduplicatePatternsFallback = (patterns: Pattern[]): Pattern[] => {
  const uniquePatterns: Pattern[] = [];

  const sorted = [...patterns].sort((a, b) => b.strength - a.strength);

  // Helper: Calculate word overlap ratio
  const calculateOverlap = (title1: string, title2: string): number => {
    const words1 = new Set(title1.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    const words2 = new Set(title2.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    if (words1.size === 0 || words2.size === 0) return 0;
    const overlap = [...words1].filter(w => words2.has(w)).length;
    return overlap / Math.min(words1.size, words2.size);
  };

  // Helper: Check if descriptions are similar
  const areDescriptionsSimilar = (desc1: string, desc2: string): boolean => {
    if (!desc1 || !desc2) return false;
    const words1 = new Set(desc1.toLowerCase().split(/\s+/).filter(w => w.length > 3));
    const words2 = new Set(desc2.toLowerCase().split(/\s+/).filter(w => w.length > 3));
    if (words1.size === 0 || words2.size === 0) return false;
    const overlap = [...words1].filter(w => words2.has(w)).length;
    return overlap >= 3 || (overlap / Math.min(words1.size, words2.size) >= 0.4);
  };

  for (const pattern of sorted) {
    const similarIndex = uniquePatterns.findIndex(existing => {
      // Same type check (patterns of different types are unlikely to be duplicates)
      if (existing.type !== pattern.type) return false;

      // Check title overlap (lowered threshold from 2 words to ratio-based)
      const titleOverlap = calculateOverlap(pattern.title, existing.title);
      if (titleOverlap >= 0.5) return true;

      // Check description similarity for same-type patterns
      if (areDescriptionsSimilar(pattern.description, existing.description)) return true;

      // Check if one title contains the other
      const t1 = pattern.title.toLowerCase();
      const t2 = existing.title.toLowerCase();
      if (t1.includes(t2) || t2.includes(t1)) return true;

      return false;
    });

    if (similarIndex === -1) {
      uniquePatterns.push(pattern);
    } else {
      // Merge into existing pattern (stronger one is already in the list)
      const existing = uniquePatterns[similarIndex];
      uniquePatterns[similarIndex] = {
        ...existing,
        relatedEntries: [...new Set([...existing.relatedEntries, ...pattern.relatedEntries])],
        frequency: Math.max(existing.frequency, pattern.frequency),
        strength: Math.max(existing.strength, pattern.strength),
        firstSeen: Math.min(existing.firstSeen, pattern.firstSeen),
        lastSeen: Math.max(existing.lastSeen, pattern.lastSeen)
      };
    }
  }

  return uniquePatterns;
};

export const detectPatterns = async (
  entries: JournalEntry[],
  decisions: Decision[],
  context: LifeContext,
  existingPatterns: Pattern[] = []
): Promise<Pattern[]> => {
  if (entries.length < 5) return existingPatterns;

  const prompts = getPrompts(context.language);
  const pd = prompts.patternDetection;

  const userCoreContext = `
USER'S CORE ELEMENTS (patterns MUST connect to these):
- VALUES: ${context.values.length > 0 ? context.values.join(', ') : 'Not defined yet'}
- GOALS: ${context.goals.length > 0 ? context.goals.join(', ') : 'Not defined yet'}
- BLOCKERS: ${context.blockers.length > 0 ? context.blockers.join(', ') : 'None identified'}
- LIFE AREAS: Health, Focus, Connection, Spirit, Finance`;

  const systemPrompt = `${pd.systemRole.replace('{userName}', context.userName || '')}

${pd.task}

${userCoreContext}

${pd.patternTypes}
1. ${pd.emotional}
2. ${pd.behavioral}
3. ${pd.temporal}
4. ${pd.relational}
5. ${pd.decision}

${pd.historyIntro} (${entries.length}):
${entries.slice(0, 100).map(e =>
  `[${new Date(e.timestamp).toLocaleDateString()}] ${e.content} (${e.emotion}, ${e.category}, alignment: ${e.goalAlignment}%)`
).join('\n')}
${entries.length > 100 ? `\n... + ${entries.length - 100} more in: ${[...new Set(entries.slice(100).map(e => e.category))].join(', ')}` : ''}

${pd.decisionsIntro} (${decisions.length}):
${decisions.slice(0, 30).map(d =>
  `[${new Date(d.timestamp).toLocaleDateString()}] ${d.title} - ${d.outcome?.chosenOption || 'pending'} - ${d.outcome?.outcome || 'unknown'}`
).join('\n')}

${pd.existingPatterns}
${existingPatterns.map(p => `- ${p.title} (${p.frequency}x, strength: ${p.strength}%)`).join('\n') || pd.none}

${pd.responseFormat}

${pd.rules}`;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: pd.userMessage }
      ],
      { jsonMode: true, maxTokens: 4096 }
    );

    const result = JSON.parse(content || '{"patterns":[]}');

    const rawPatterns: Pattern[] = result.patterns
      .filter((p: any) => ((p.relatedEntryIds?.length || p.frequency || 1) >= 2) && (p.strength || 50) >= 40)
      .map((p: any) => {
        const newTitle = (p.title || 'Pattern').toLowerCase();
        const newType = p.type as PatternType;
        
        const existingMatch = existingPatterns.find(existing => {
          if (existing.type !== newType) return false;
          const existingTitle = existing.title.toLowerCase();
          if (existingTitle === newTitle) return true;
          const words1 = new Set<string>(newTitle.split(/\s+/).filter((w: string) => w.length > 3));
          const words2 = new Set<string>(existingTitle.split(/\s+/).filter((w: string) => w.length > 3));
          const overlap = [...words1].filter((w: string) => words2.has(w)).length;
          return overlap >= 2 || (words1.size > 0 && overlap / words1.size >= 0.6);
        });
        
        const patternId = existingMatch?.id || Math.random().toString(36).substr(2, 9);
        const existingEntries = existingMatch?.relatedEntries || [];
        const newEntries = p.relatedEntryIds || [];
        const mergedEntries = [...new Set([...existingEntries, ...newEntries])];
        
        // Calculate frequency from actual related entries, not AI's arbitrary number
        const calculatedFrequency = mergedEntries.length > 0 ? mergedEntries.length : (p.frequency || 3);
        
        return {
          id: patternId,
          type: newType,
          title: p.title || 'Pattern',
          description: p.description || existingMatch?.description || '',
          frequency: Math.max(1, calculatedFrequency),
          firstSeen: existingMatch?.firstSeen || entries[entries.length - 1]?.timestamp || Date.now(),
          lastSeen: entries[0]?.timestamp || Date.now(),
          relatedEntries: mergedEntries,
          insight: p.insight || existingMatch?.insight || '',
          isPositive: p.isPositive ?? existingMatch?.isPositive ?? true,
          strength: Math.max(40, Math.min(100, p.strength || existingMatch?.strength || 50)),
          coreConnection: p.coreConnection ? {
            type: p.coreConnection.type || 'life_area',
            name: p.coreConnection.name || 'General',
            alignment: Math.max(-100, Math.min(100, p.coreConnection.alignment || 0))
          } : existingMatch?.coreConnection
        };
      });

    const deduplicated = await deduplicatePatternsWithEmbeddings(rawPatterns, existingPatterns);
    const MAX_PATTERNS = 12;

    // Smart pattern selection for variety:
    // 1. Ensure mix of pattern types (emotional, behavioral, temporal, relational, decision)
    // 2. Mix of positive and negative patterns
    // 3. Mix of strong and moderate patterns (not just top strength)

    const patternsByType: Record<string, Pattern[]> = {};
    deduplicated.forEach(p => {
      if (!patternsByType[p.type]) patternsByType[p.type] = [];
      patternsByType[p.type].push(p);
    });

    // Select top patterns from each type to ensure variety
    const selectedPatterns: Pattern[] = [];
    const types = Object.keys(patternsByType);

    // First pass: take top 2 from each type
    types.forEach(type => {
      const typePatterns = patternsByType[type].sort((a, b) => b.strength - a.strength);
      selectedPatterns.push(...typePatterns.slice(0, 2));
    });

    // Second pass: fill remaining slots with strongest overall
    const remaining = deduplicated
      .filter(p => !selectedPatterns.some(s => s.id === p.id))
      .sort((a, b) => b.strength - a.strength);

    while (selectedPatterns.length < MAX_PATTERNS && remaining.length > 0) {
      selectedPatterns.push(remaining.shift()!);
    }

    // Final sort by strength for display
    const topPatterns = selectedPatterns
      .sort((a: Pattern, b: Pattern) => b.strength - a.strength)
      .slice(0, MAX_PATTERNS);

    // Log variety stats
    const typeStats = types.map(t => `${t}: ${topPatterns.filter(p => p.type === t).length}`).join(', ');
    const positiveCount = topPatterns.filter(p => p.isPositive).length;
    console.log(`📊 Pattern analysis: ${rawPatterns.length} new → ${deduplicated.length} deduplicated → ${topPatterns.length} final (${typeStats}, +${positiveCount}/-${topPatterns.length - positiveCount})`);

    return topPatterns;
  } catch (error) {
    console.error('Pattern detection error:', error);
    return existingPatterns;
  }
};

// ============================================
// INTELLIGENT ENTRY SELECTION
// ============================================

const selectRelevantEntries = (
  newEntry: JournalEntry,
  existingEntries: JournalEntry[],
  maxEntries: number = 50
): { selected: JournalEntry[], historySummary: string } => {
  if (existingEntries.length === 0) return { selected: [], historySummary: '' };
  
  // Ensure entries are sorted by timestamp descending (newest first)
  const sortedEntries = [...existingEntries].sort((a, b) => b.timestamp - a.timestamp);
  
  const selectedIds = new Set<string>();
  const selected: JournalEntry[] = [];
  
  const addEntry = (entry: JournalEntry) => {
    if (!selectedIds.has(entry.id) && selected.length < maxEntries) {
      selectedIds.add(entry.id);
      selected.push(entry);
    }
  };

  // 1. Recent entries (last 20) - most important for context
  sortedEntries.slice(0, 20).forEach(addEntry);

  // 2. Same category entries (for thematic relevance)
  sortedEntries
    .filter(e => e.category === newEntry.category && !selectedIds.has(e.id))
    .slice(0, 10)
    .forEach(addEntry);

  // 3. Same emotion entries (for emotional patterns)
  sortedEntries
    .filter(e => e.emotion === newEntry.emotion && !selectedIds.has(e.id))
    .slice(0, 5)
    .forEach(addEntry);

  // 4. High impact entries (goalAlignment > 80 or < 30)
  sortedEntries
    .filter(e => (e.goalAlignment > 80 || e.goalAlignment < 30) && !selectedIds.has(e.id))
    .slice(0, 10)
    .forEach(addEntry);

  // 5. Entries with existing connections (graph nodes with edges)
  sortedEntries
    .filter(e => e.relatedTo && e.relatedTo.length > 0 && !selectedIds.has(e.id))
    .slice(0, 5)
    .forEach(addEntry);

  // Generate historical summary for entries not included
  const excludedEntries = sortedEntries.filter(e => !selectedIds.has(e.id));
  let historySummary = '';
  
  if (excludedEntries.length > 0) {
    const categoryStats: Record<string, { count: number, avgAlignment: number, emotions: string[] }> = {};
    
    excludedEntries.forEach(e => {
      if (!categoryStats[e.category]) {
        categoryStats[e.category] = { count: 0, avgAlignment: 0, emotions: [] };
      }
      categoryStats[e.category].count++;
      categoryStats[e.category].avgAlignment += e.goalAlignment;
      if (!categoryStats[e.category].emotions.includes(e.emotion)) {
        categoryStats[e.category].emotions.push(e.emotion);
      }
    });

    const summaryParts = Object.entries(categoryStats).map(([cat, stats]) => {
      const avgAlign = Math.round(stats.avgAlignment / stats.count);
      return `${cat}: ${stats.count} momenti (avg alignment: ${avgAlign}%, emotions: ${stats.emotions.slice(0, 3).join(' ')})`;
    });

    historySummary = `\n\nSTORIA PRECEDENTE (${excludedEntries.length} momenti non mostrati):\n${summaryParts.join('\n')}`;
  }

  return { selected, historySummary };
};

// ============================================
// SEMANTIC LINKING (DEEP DATASET SEARCH)
// ============================================

interface UnifiedSearchItem {
  id: string;
  type: 'entry' | 'decision' | 'person';
  content: string;
  metadata: string;
  originalIndex: number;
}

const buildUnifiedDataset = (
  entries: JournalEntry[],
  decisions: Decision[],
  context: LifeContext
): UnifiedSearchItem[] => {
  const items: UnifiedSearchItem[] = [];
  
  // Add journal entries
  entries.forEach((e, idx) => {
    items.push({
      id: e.id,
      type: 'entry',
      content: e.content,
      metadata: `${e.emotion} | ${e.category} | ${new Date(e.timestamp).toLocaleDateString()} | alignment: ${e.goalAlignment}%`,
      originalIndex: idx
    });
  });
  
  // Add decisions
  decisions.forEach((d, idx) => {
    const optionsSummary = d.options.map(o => `${o.title}: ${o.pros.join(', ')}`).join('; ');
    const outcomeInfo = d.outcome ? ` | Outcome: ${d.outcome.outcome}, Lessons: ${d.outcome.lessonsLearned.join('; ')}` : '';
    const dateStr = d.timestamp ? new Date(d.timestamp).toLocaleDateString() : 'Unknown date';
    items.push({
      id: d.id,
      type: 'decision',
      content: `${d.title}: ${d.context}. Options: ${optionsSummary}${outcomeInfo}`,
      metadata: `Decision | ${dateStr}${d.outcome ? ' | Resolved' : ' | Open'}`,
      originalIndex: idx
    });
  });
  
  // Add key people from context
  if (context.keyPeople && context.keyPeople.length > 0) {
    context.keyPeople.forEach((p, idx) => {
      items.push({
        id: `person_${p.name.toLowerCase().replace(/\s+/g, '_')}`,
        type: 'person',
        content: `${p.name}: ${p.relationship}. Interaction frequency: ${p.frequency}, mood impact: ${p.avgMoodImpact > 0 ? '+' : ''}${p.avgMoodImpact}`,
        metadata: `Person | ${p.relationship} | Frequency: ${p.frequency}`,
        originalIndex: idx
      });
    });
  }
  
  return items;
};

const findTopSemanticMatches = async (
  queryText: string,
  items: UnifiedSearchItem[],
  maxResults: number = 30
): Promise<UnifiedSearchItem[]> => {
  if (items.length === 0) return [];
  if (items.length <= maxResults) return items;
  
  try {
    // Get embedding for the query
    const queryEmbedding = await aiEmbedding(queryText);
    if (!queryEmbedding || queryEmbedding.length === 0) {
      console.log('Query embedding failed, using fallback selection');
      return items.slice(0, maxResults);
    }
    
    // Get embeddings for all items (in batches)
    const itemTexts = items.map(i => `${i.content} ${i.metadata}`);
    const batchSize = 50; // Smaller batches for reliability
    const allEmbeddings: number[][] = [];
    
    for (let i = 0; i < itemTexts.length; i += batchSize) {
      const batch = itemTexts.slice(i, i + batchSize);
      try {
        const batchEmbeddings = await aiBatchEmbeddings(batch);
        if (batchEmbeddings && batchEmbeddings.length === batch.length) {
          allEmbeddings.push(...batchEmbeddings);
        } else {
          // If batch fails, fill with empty embeddings
          console.log(`Batch embedding incomplete at index ${i}, using fallback`);
          for (let j = 0; j < batch.length; j++) {
            allEmbeddings.push([]);
          }
        }
      } catch (batchError) {
        console.error(`Batch embedding error at index ${i}:`, batchError);
        // Fill with empty embeddings for failed batch
        for (let j = 0; j < batch.length; j++) {
          allEmbeddings.push([]);
        }
      }
    }
    
    // Filter items that have valid embeddings
    const scored = items.map((item, idx) => {
      const embedding = allEmbeddings[idx];
      const hasValidEmbedding = embedding && embedding.length > 0 && embedding.length === queryEmbedding.length;
      return {
        item,
        similarity: hasValidEmbedding ? cosineSimilarity(queryEmbedding, embedding) : 0
      };
    });
    
    // Sort by similarity and return top matches
    scored.sort((a, b) => b.similarity - a.similarity);
    return scored.slice(0, maxResults).map(s => s.item);
  } catch (error) {
    console.error('Semantic search error:', error);
    return items.slice(0, maxResults);
  }
};

export const findSemanticLinks = async (
  newEntry: JournalEntry,
  existingEntries: JournalEntry[],
  context: LifeContext,
  decisions: Decision[] = []
): Promise<SemanticLink[]> => {
  if (existingEntries.length === 0 && decisions.length === 0) return [];

  const prompts = getPrompts(context.language);
  const sl = prompts.semanticLinks;

  // Build unified dataset from entries, decisions, and people
  const unifiedDataset = buildUnifiedDataset(existingEntries, decisions, context);
  
  // Find top semantic matches using embeddings
  const topMatches = await findTopSemanticMatches(newEntry.content, unifiedDataset, 50);
  
  // Also include items from selectRelevantEntries for comprehensive coverage
  const { selected: relevantEntries, historySummary } = selectRelevantEntries(newEntry, existingEntries, 20);
  
  // Merge and deduplicate
  const seenIds = new Set(topMatches.map(m => m.id));
  const additionalFromRelevant = relevantEntries
    .filter(e => !seenIds.has(e.id))
    .map((e, idx) => ({
      id: e.id,
      type: 'entry' as const,
      content: e.content,
      metadata: `${e.emotion} | ${e.category} | ${new Date(e.timestamp).toLocaleDateString()} | alignment: ${e.goalAlignment}%`,
      originalIndex: idx
    }));
  
  const finalDataset = [...topMatches, ...additionalFromRelevant].slice(0, 60);

  const systemPrompt = `${sl.systemRole.replace('{userName}', context.userName || '')}

${sl.newMomentIntro}
"${newEntry.content}" (${newEntry.emotion}, ${newEntry.category}, ${new Date(newEntry.timestamp).toLocaleDateString()})

FULL USER DATASET (entries, decisions, people - ${unifiedDataset.length} total items):
${finalDataset.map((item, idx) =>
  `[${idx}] [${item.type.toUpperCase()}] ${item.content} (${item.metadata})`
).join('\n')}
${historySummary}

${sl.task}

LINK TYPES (expanded for full dataset):
- ${sl.similar}
- ${sl.cause}
- ${sl.effect}
- ${sl.contradiction}
- ${sl.progression}
- ${sl.relatedDecision}
- ${sl.relatedPerson}
- ${sl.patternEvidence}

${sl.responseFormat.replace('{maxIndex}', String(finalDataset.length - 1))}

RULES:
- Search across ALL item types (entries, decisions, people)
- Prioritize deep semantic connections over surface-level similarities
- Include cross-type connections (e.g., moment → decision, moment → person)
- Return up to 10 strongest connections
- Consider temporal patterns and recurring themes`;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: sl.userMessage }
      ],
      { jsonMode: true, maxTokens: 1024 }
    );

    const result = JSON.parse(content || '{"links":[]}');

    // Convert indices to IDs
    const links: SemanticLink[] = result.links
      .filter((l: any) => l.strength >= 50)
      .map((l: any) => ({
        targetEntryId: finalDataset[l.targetIndex]?.id,
        linkType: l.linkType as LinkType,
        strength: l.strength,
        reason: l.reason
      }))
      .filter((l: SemanticLink) => l.targetEntryId);

    return links;
  } catch (error) {
    console.error('Semantic linking error:', error);
    return [];
  }
};

// ============================================
// DECISION SIMILARITY ANALYSIS
// ============================================

export const findSimilarDecisions = async (
  newDecision: Decision,
  pastDecisions: Decision[],
  context: LifeContext
): Promise<Decision['similarPastDecisions']> => {
  const decisionsWithOutcomes = pastDecisions.filter(d => d.outcome);
  if (decisionsWithOutcomes.length === 0) return [];

  const lang = context.language || 'English';
  const decisionsListFormatted = decisionsWithOutcomes.map((d, idx) => {
    const labels: Record<string, { choice: string; outcome: string; lessons: string }> = {
      Italian: { choice: 'Scelta', outcome: 'Esito', lessons: 'Lezioni' },
      English: { choice: 'Choice', outcome: 'Outcome', lessons: 'Lessons' },
      Spanish: { choice: 'Elección', outcome: 'Resultado', lessons: 'Lecciones' },
      German: { choice: 'Wahl', outcome: 'Ergebnis', lessons: 'Lektionen' },
      French: { choice: 'Choix', outcome: 'Résultat', lessons: 'Leçons' }
    };
    const l = labels[lang] || labels.English;
    return `[${idx}] "${d.title}" - ${l.choice}: ${d.outcome!.chosenOption} - ${l.outcome}: ${d.outcome!.outcome} - ${l.lessons}: ${d.outcome!.lessonsLearned.join('; ')}`;
  }).join('\n');

  const prompts: Record<string, { system: string; user: string }> = {
    Italian: {
      system: `Sei un analista di decisioni per ${context.userName}.

NUOVA DECISIONE:
"${newDecision.title}"
Contesto: ${newDecision.context}
Opzioni: ${newDecision.options.map(o => o.title).join(', ')}

DECISIONI PASSATE (con outcome):
${decisionsListFormatted}

COMPITO: Trova decisioni passate simili che possono informare questa nuova scelta.

Rispondi in JSON:
{
  "similarDecisions": [
    {
      "decisionIndex": numero,
      "similarity": 0-100,
      "outcome": "positive" | "negative" | "neutral",
      "lesson": "Lezione chiave"
    }
  ]
}

REGOLE:
- Identifica SOLO decisioni veramente simili (similarity >= 70)
- Focus su lezioni ACTIONABLE
- Max 3 decisioni simili`,
      user: 'Trova decisioni simili e lezioni apprese.'
    },
    English: {
      system: `You are a decision analyst for ${context.userName}.

NEW DECISION:
"${newDecision.title}"
Context: ${newDecision.context}
Options: ${newDecision.options.map(o => o.title).join(', ')}

PAST DECISIONS (with outcomes):
${decisionsListFormatted}

TASK: Find similar past decisions that can inform this new choice.

Respond in JSON:
{
  "similarDecisions": [
    {
      "decisionIndex": number,
      "similarity": 0-100,
      "outcome": "positive" | "negative" | "neutral",
      "lesson": "Key lesson"
    }
  ]
}

RULES:
- Identify ONLY truly similar decisions (similarity >= 70)
- Focus on ACTIONABLE lessons
- Max 3 similar decisions`,
      user: 'Find similar decisions and lessons learned.'
    },
    Spanish: {
      system: `Eres un analista de decisiones para ${context.userName}.

NUEVA DECISIÓN:
"${newDecision.title}"
Contexto: ${newDecision.context}
Opciones: ${newDecision.options.map(o => o.title).join(', ')}

DECISIONES PASADAS (con resultados):
${decisionsListFormatted}

TAREA: Encuentra decisiones pasadas similares que puedan informar esta nueva elección.

Responde en JSON:
{
  "similarDecisions": [
    {
      "decisionIndex": número,
      "similarity": 0-100,
      "outcome": "positive" | "negative" | "neutral",
      "lesson": "Lección clave"
    }
  ]
}

REGLAS:
- Identifica SOLO decisiones realmente similares (similarity >= 70)
- Enfócate en lecciones ACCIONABLES
- Máximo 3 decisiones similares`,
      user: 'Encuentra decisiones similares y lecciones aprendidas.'
    },
    German: {
      system: `Du bist ein Entscheidungsanalyst für ${context.userName}.

NEUE ENTSCHEIDUNG:
"${newDecision.title}"
Kontext: ${newDecision.context}
Optionen: ${newDecision.options.map(o => o.title).join(', ')}

VERGANGENE ENTSCHEIDUNGEN (mit Ergebnissen):
${decisionsListFormatted}

AUFGABE: Finde ähnliche vergangene Entscheidungen, die diese neue Wahl beeinflussen können.

Antworte in JSON:
{
  "similarDecisions": [
    {
      "decisionIndex": Zahl,
      "similarity": 0-100,
      "outcome": "positive" | "negative" | "neutral",
      "lesson": "Schlüssellektion"
    }
  ]
}

REGELN:
- Identifiziere NUR wirklich ähnliche Entscheidungen (similarity >= 70)
- Fokus auf UMSETZBARE Lektionen
- Maximal 3 ähnliche Entscheidungen`,
      user: 'Finde ähnliche Entscheidungen und gelernte Lektionen.'
    },
    French: {
      system: `Tu es un analyste de décisions pour ${context.userName}.

NOUVELLE DÉCISION:
"${newDecision.title}"
Contexte: ${newDecision.context}
Options: ${newDecision.options.map(o => o.title).join(', ')}

DÉCISIONS PASSÉES (avec résultats):
${decisionsListFormatted}

TÂCHE: Trouve des décisions passées similaires qui peuvent informer ce nouveau choix.

Réponds en JSON:
{
  "similarDecisions": [
    {
      "decisionIndex": nombre,
      "similarity": 0-100,
      "outcome": "positive" | "negative" | "neutral",
      "lesson": "Leçon clé"
    }
  ]
}

RÈGLES:
- Identifie SEULEMENT les décisions vraiment similaires (similarity >= 70)
- Focus sur les leçons ACTIONNABLES
- Maximum 3 décisions similaires`,
      user: 'Trouve des décisions similaires et les leçons apprises.'
    }
  };

  const p = prompts[lang] || prompts.English;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: p.system },
        { role: 'user', content: p.user }
      ],
      { jsonMode: true, maxTokens: 1024 }
    );

    const result = JSON.parse(content || '{"similarDecisions":[]}');

    return result.similarDecisions.map((s: any) => ({
      decisionId: decisionsWithOutcomes[s.decisionIndex]?.id,
      similarity: s.similarity,
      outcome: s.outcome,
      lesson: s.lesson
    })).filter((s: any) => s.decisionId);
  } catch (error) {
    console.error('Decision similarity error:', error);
    return [];
  }
};

// ============================================
// WARNING SYSTEM
// ============================================

export const checkForRepeatedMistakes = async (
  currentContext: string,
  decisions: Decision[],
  entries: JournalEntry[],
  userContext: LifeContext
): Promise<string | null> => {
  const negativeOutcomes = decisions.filter(d =>
    d.outcome && (d.outcome.outcome === 'negative' || d.outcome.outcome === 'mixed')
  );

  if (negativeOutcomes.length === 0) return null;

  const lang = userContext.language || 'English';
  const labels: Record<string, { choice: string; outcome: string; lessons: string }> = {
    Italian: { choice: 'Scelta', outcome: 'Esito', lessons: 'Lezioni' },
    English: { choice: 'Choice', outcome: 'Outcome', lessons: 'Lessons' },
    Spanish: { choice: 'Elección', outcome: 'Resultado', lessons: 'Lecciones' },
    German: { choice: 'Wahl', outcome: 'Ergebnis', lessons: 'Lektionen' },
    French: { choice: 'Choix', outcome: 'Résultat', lessons: 'Leçons' }
  };
  const l = labels[lang] || labels.English;

  const negativeDecisionsFormatted = negativeOutcomes.map(d =>
    `- "${d.title}" → ${l.choice}: ${d.outcome!.chosenOption} → ${l.outcome}: ${d.outcome!.outcomeSummary} → ${l.lessons}: ${d.outcome!.lessonsLearned.join('; ')}`
  ).join('\n');

  const negativePatterns = entries.slice(0, 10)
    .filter(e => e.goalAlignment < 40)
    .map(e => `- ${e.content} (${e.emotion}, alignment: ${e.goalAlignment}%)`)
    .join('\n');

  const prompts: Record<string, { system: string; user: string }> = {
    Italian: {
      system: `Sei un sistema di early warning per ${userContext.userName}.

SITUAZIONE ATTUALE:
"${currentContext}"

DECISIONI PASSATE CON ESITI NEGATIVI:
${negativeDecisionsFormatted}

PATTERN NEGATIVI NEI MOMENTI RECENTI:
${negativePatterns}

COMPITO: Determina se la situazione attuale mostra segni di ripetizione di errori passati.

Rispondi in JSON:
{
  "isRepeating": true/false,
  "warning": "Messaggio di warning specifico e empatico (se isRepeating=true)",
  "suggestion": "Suggerimento concreto basato sulle lezioni apprese"
}

REGOLE:
- Sii SPECIFICO, non generico
- Riferisciti a decisioni/situazioni concrete del passato
- Tono empatico ma diretto
- Focus su azione preventiva`,
      user: 'Analizza e avvisa se necessario.'
    },
    English: {
      system: `You are an early warning system for ${userContext.userName}.

CURRENT SITUATION:
"${currentContext}"

PAST DECISIONS WITH NEGATIVE OUTCOMES:
${negativeDecisionsFormatted}

NEGATIVE PATTERNS IN RECENT MOMENTS:
${negativePatterns}

TASK: Determine if the current situation shows signs of repeating past mistakes.

Respond in JSON:
{
  "isRepeating": true/false,
  "warning": "Specific and empathetic warning message (if isRepeating=true)",
  "suggestion": "Concrete suggestion based on lessons learned"
}

RULES:
- Be SPECIFIC, not generic
- Refer to concrete past decisions/situations
- Empathetic but direct tone
- Focus on preventive action`,
      user: 'Analyze and warn if necessary.'
    },
    Spanish: {
      system: `Eres un sistema de alerta temprana para ${userContext.userName}.

SITUACIÓN ACTUAL:
"${currentContext}"

DECISIONES PASADAS CON RESULTADOS NEGATIVOS:
${negativeDecisionsFormatted}

PATRONES NEGATIVOS EN MOMENTOS RECIENTES:
${negativePatterns}

TAREA: Determina si la situación actual muestra signos de repetición de errores pasados.

Responde en JSON:
{
  "isRepeating": true/false,
  "warning": "Mensaje de advertencia específico y empático (si isRepeating=true)",
  "suggestion": "Sugerencia concreta basada en lecciones aprendidas"
}

REGLAS:
- Sé ESPECÍFICO, no genérico
- Refiere a decisiones/situaciones concretas del pasado
- Tono empático pero directo
- Enfoque en acción preventiva`,
      user: 'Analiza y advierte si es necesario.'
    },
    German: {
      system: `Du bist ein Frühwarnsystem für ${userContext.userName}.

AKTUELLE SITUATION:
"${currentContext}"

VERGANGENE ENTSCHEIDUNGEN MIT NEGATIVEN ERGEBNISSEN:
${negativeDecisionsFormatted}

NEGATIVE MUSTER IN JÜNGSTEN MOMENTEN:
${negativePatterns}

AUFGABE: Bestimme, ob die aktuelle Situation Anzeichen für wiederholte Fehler zeigt.

Antworte in JSON:
{
  "isRepeating": true/false,
  "warning": "Spezifische und empathische Warnmeldung (wenn isRepeating=true)",
  "suggestion": "Konkreter Vorschlag basierend auf gelernten Lektionen"
}

REGELN:
- Sei SPEZIFISCH, nicht generisch
- Beziehe dich auf konkrete vergangene Entscheidungen/Situationen
- Empathischer aber direkter Ton
- Fokus auf präventive Maßnahmen`,
      user: 'Analysiere und warne falls nötig.'
    },
    French: {
      system: `Tu es un système d'alerte précoce pour ${userContext.userName}.

SITUATION ACTUELLE:
"${currentContext}"

DÉCISIONS PASSÉES AVEC RÉSULTATS NÉGATIFS:
${negativeDecisionsFormatted}

PATTERNS NÉGATIFS DANS LES MOMENTS RÉCENTS:
${negativePatterns}

TÂCHE: Détermine si la situation actuelle montre des signes de répétition d'erreurs passées.

Réponds en JSON:
{
  "isRepeating": true/false,
  "warning": "Message d'avertissement spécifique et empathique (si isRepeating=true)",
  "suggestion": "Suggestion concrète basée sur les leçons apprises"
}

RÈGLES:
- Sois SPÉCIFIQUE, pas générique
- Réfère-toi à des décisions/situations concrètes du passé
- Ton empathique mais direct
- Focus sur l'action préventive`,
      user: 'Analyse et avertis si nécessaire.'
    }
  };

  const p = prompts[lang] || prompts.English;

  try {
    const content = await aiChatCompletion(
      [
        { role: 'system', content: p.system },
        { role: 'user', content: p.user }
      ],
      { jsonMode: true, maxTokens: 512 }
    );

    const result = JSON.parse(content || '{"isRepeating":false}');

    if (result.isRepeating && result.warning) {
      return `⚠️ ${result.warning}\n\n💡 ${result.suggestion}`;
    }

    return null;
  } catch (error) {
    console.error('Warning system error:', error);
    return null;
  }
};

// ============================================
// EXTRACT PEOPLE FROM CONTENT
// ============================================

export const extractPeople = async (
  content: string,
  imageBase64?: string,
  language: LifeContext['language'] = 'English'
): Promise<Array<{ name: string; relationship?: string }>> => {
  const prompts = getPrompts(language);
  const ep = prompts.extractPeople;
  
  const systemPrompt = `${ep.systemRole}

${ep.responseFormat}

${ep.rules}`;

  try {
    const userMessage: ChatMessage = imageBase64
      ? {
          role: 'user',
          content: [
            { type: 'text', text: content },
            { type: 'image_url', image_url: { url: imageBase64, detail: 'low' } }
          ]
        }
      : { role: 'user', content };

    const responseContent = await aiChatCompletion(
      [{ role: 'system', content: systemPrompt }, userMessage],
      { jsonMode: true, maxTokens: 256, hasImage: !!imageBase64 }
    );

    const result = JSON.parse(responseContent || '{"people":[]}');
    return result.people || [];
  } catch (error) {
    console.error('People extraction error:', error);
    return [];
  }
};

// ============================================
// PATTERN STRENGTH DECAY/GROWTH
// ============================================

export const updatePatternStrengths = (
  patterns: Pattern[],
  entries: JournalEntry[]
): Pattern[] => {
  const now = Date.now();
  const dayInMs = 1000 * 60 * 60 * 24;
  
  return patterns.map(pattern => {
    const daysSinceLastSeen = (now - pattern.lastSeen) / dayInMs;
    const recentEntries = entries.filter(e => 
      pattern.relatedEntries.includes(e.id) && 
      (now - e.timestamp) < dayInMs * 7
    );
    
    let strengthDelta = 0;
    
    // Decay: reduce strength if pattern not seen recently
    if (daysSinceLastSeen > 7) {
      strengthDelta -= Math.min(10, Math.floor(daysSinceLastSeen / 7) * 2);
    }
    
    // Growth: increase strength if pattern reinforced by recent entries
    if (recentEntries.length > 0) {
      strengthDelta += recentEntries.length * 3;
    }
    
    // Update frequency based on related entries
    const newFrequency = entries.filter(e => 
      pattern.relatedEntries.includes(e.id)
    ).length;
    
    // Calculate new strength (clamped 0-100)
    const newStrength = Math.max(0, Math.min(100, pattern.strength + strengthDelta));
    
    return {
      ...pattern,
      strength: newStrength,
      frequency: Math.max(pattern.frequency, newFrequency),
      lastSeen: recentEntries.length > 0 ? Math.max(...recentEntries.map(e => e.timestamp)) : pattern.lastSeen
    };
  }).filter(p => p.strength > 10); // Remove patterns that have decayed too much
};

// ============================================
// GRAPH ANALYSIS SUMMARY
// ============================================

export const getGraphInsights = (
  entries: JournalEntry[],
  decisions: Decision[],
  patterns: Pattern[]
): string => {
  const totalLinks = entries.reduce((sum, e) => sum + (e.relatedTo?.length || 0), 0);
  const strongPatterns = patterns.filter(p => p.strength >= 70);
  const weakPatterns = patterns.filter(p => p.strength < 70);
  const positivePatterns = patterns.filter(p => p.isPositive);
  const negativePatterns = patterns.filter(p => !p.isPositive);

  const decisionsWithOutcomes = decisions.filter(d => d.outcome);
  const positiveDecisions = decisionsWithOutcomes.filter(d => d.outcome!.outcome === 'positive');
  const negativeDecisions = decisionsWithOutcomes.filter(d => d.outcome!.outcome === 'negative');

  return `GRAFO SEMANTICO DELLA VITA:
• ${entries.length} momenti tracciati
• ${totalLinks} collegamenti semantici
• ${patterns.length} pattern identificati (${strongPatterns.length} forti, ${weakPatterns.length} deboli)
• ${positivePatterns.length} pattern positivi, ${negativePatterns.length} pattern da gestire
• ${decisionsWithOutcomes.length}/${decisions.length} decisioni con outcome registrato
• Success rate: ${decisionsWithOutcomes.length > 0 ? Math.round((positiveDecisions.length / decisionsWithOutcomes.length) * 100) : 0}%

PATTERN PIÙ FORTI:
${strongPatterns.slice(0, 3).map(p => `• ${p.title} (${p.frequency}x, ${p.isPositive ? '✅' : '⚠️'})`).join('\n') || '• Nessuno ancora'}`;
};
